// src/hooks/useWellIntel.js
// FlowSense MOW+ — live Well & Drainage Intelligence for a page.
//   • loads every well in the register once (GET /wells/intel)
//   • WebSocket `well_intel_update` patches one well in place (pushed by the telemetry worker / inspections)
//   • `sensor_events` (link / unlink / create) and `{ refresh: true }` pushes trigger a coalesced reload
//   • safety re-sync every 2 min while the tab is visible; requests are aborted on unmount
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import webSocketService from '../services/websocketService';
import { fetchWellFleetIntel } from '../services/wellIntelService';

const RESYNC_MS = 120000;
const RELOAD_COALESCE_MS = 1500;
const LIVE_EVENT_MAX = 20;

export function useWellIntel({ enabled = true } = {}) {
  const [wells, setWells] = useState([]);
  const [summary, setSummary] = useState(null);
  const [generatedAt, setGeneratedAt] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [liveEvents, setLiveEvents] = useState([]);
  const abortRef = useRef(null);
  const reloadTimer = useRef(null);
  const mounted = useRef(true);

  const load = useCallback(async ({ force = false } = {}) => {
    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    try {
      const data = await fetchWellFleetIntel({ force, signal: ctrl.signal });
      if (!mounted.current || ctrl.signal.aborted) return;
      setWells(Array.isArray(data?.wells) ? data.wells : []);
      setSummary(data?.summary || null);
      setGeneratedAt(data?.generated_at || new Date().toISOString());
      setError(null);
    } catch (e) {
      if (ctrl.signal.aborted || e?.name === 'CanceledError' || e?.name === 'AbortError') return;
      if (mounted.current) setError(e?.response?.data?.message || e?.message || 'Could not load well intelligence');
    } finally {
      if (mounted.current && !ctrl.signal.aborted) setIsLoading(false);
    }
  }, []);

  const scheduleReload = useCallback(() => {
    if (reloadTimer.current) return;
    reloadTimer.current = setTimeout(() => { reloadTimer.current = null; load(); }, RELOAD_COALESCE_MS);
  }, [load]);

  useEffect(() => {
    mounted.current = true;
    if (!enabled) return undefined;
    load();

    const onIntel = (msg) => {
      const p = msg?.payload ?? msg?.data ?? msg;
      if (!p) return;
      if (p.refresh || !p.well) { scheduleReload(); return; }
      const well = p.well;
      setWells((prev) => {
        const i = prev.findIndex((w) => String(w.id) === String(well.id));
        if (i === -1) return [...prev, well];
        const next = prev.slice();
        next[i] = well;
        return next;
      });
      if (p.summary) setSummary(p.summary);
      setGeneratedAt(p.at || new Date().toISOString());
      setLiveEvents((prev) => [{ id: `${well.id}-${p.at || Date.now()}`, wellId: well.id, name: well.name, tier: well.intel?.tier, score: well.intel?.score, at: p.at || new Date().toISOString() }, ...prev].slice(0, LIVE_EVENT_MAX));
    };
    const onSensorEvent = () => scheduleReload();

    const offA = webSocketService.subscribe('well_intel_update', onIntel);
    const offB = webSocketService.subscribe('sensor_events', onSensorEvent);
    const timer = setInterval(() => {
      if (typeof document === 'undefined' || document.visibilityState !== 'hidden') load();
    }, RESYNC_MS);

    return () => {
      mounted.current = false;
      clearInterval(timer);
      clearTimeout(reloadTimer.current);
      reloadTimer.current = null;
      abortRef.current?.abort();
      if (typeof offA === 'function') offA(); else webSocketService.unsubscribe('well_intel_update', onIntel);
      if (typeof offB === 'function') offB(); else webSocketService.unsubscribe('sensor_events', onSensorEvent);
    };
  }, [enabled, load, scheduleReload]);

  const byId = useMemo(() => {
    const m = new Map();
    wells.forEach((w) => m.set(String(w.id), w));
    return m;
  }, [wells]);

  /** Server ML flood predictions keyed by well id, in the shape LiveReadingsPage's AI pipeline uses. */
  const serverPredictions = useMemo(() => {
    const out = {};
    wells.forEach((w) => {
      const f = w.flood;
      if (!f || f.probability === null || f.probability === undefined) return;
      out[String(w.id)] = {
        ...w.ai_predictions,
        wellId: w.id,
        predictedSeverity: f.severity,
        floodRiskProbability: f.probability,
        estimatedMinutesToOvertopping: f.est_minutes_to_overtopping,
        confidence: f.confidence,
        receivedAt: w.latest_reading?.timestamp ? new Date(w.latest_reading.timestamp).getTime() : Date.now(),
        source: 'server',
      };
    });
    return out;
  }, [wells]);

  const refresh = useCallback(() => load({ force: true }), [load]);

  return { wells, byId, summary, generatedAt, isLoading, error, liveEvents, serverPredictions, refresh };
}

export default useWellIntel;
